// /////////////////////////////////////////////////////////////
// [ファイル] PdfTemplateDialog.tsx
// [概要] 帳票テンプレートフォーマット管理ダイアログ。新規登録はチャットで AI に依頼し
//        (AI が名前を付けて登録)、この画面では登録済みフォーマットの名前変更・削除・
//        プレビューのみを行う (セッション履歴と同様の運用)。
//        レイアウトは固定。記述内容のデータ参照連携は後フェーズ。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import { STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import {
  usePdfTemplate,
  type BaseLayout,
  type TemplateFormat,
} from '../hooks/usePdfTemplate';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useStaffNames } from '../hooks/useStaffNames';

interface Props {
  open: boolean;
  onClose: () => void;
}

// レイアウトの表示名
const BASE_LAYOUT_LABELS: Record<BaseLayout, string> = {
  quote: '見積書（標準）',
  generic: 'カスタム帳票（AI生成）',
};

export default function PdfTemplateDialog({ open, onClose }: Props) {
  const { formats, loading, save, remove, preview } = usePdfTemplate();
  const notify = useNotification();
  const confirm = useConfirm();
  // 共有メンバー (email 配列) はツールチップで氏名表示する
  const { formatMembers } = useStaffNames();

  // 名前のインライン編集状態
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  // プレビュー表示用モーダル (Drive に保存せず PDF をその場で表示する)
  const [previewState, setPreviewState] = useState<{ url: string; name: string } | null>(null);

  // モーダルを閉じる際に Blob URL を解放する (メモリリーク防止)
  const closePreview = () => {
    setPreviewState((prev) => {
      if (prev?.url.startsWith('blob:')) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const startEdit = (f: TemplateFormat) => {
    setEditingId(f.id);
    setEditingName(f.name);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRename = async (f: TemplateFormat) => {
    const next = editingName.trim();
    if (!next) {
      notify.error('フォーマット名を入力してください');
      return;
    }
    if (next === f.name) {
      cancelEdit();
      return;
    }
    setSavingId(f.id);
    try {
      // 名前のみ更新 (レイアウト・スコープは据え置き)
      await save({ id: f.id, name: next, base_layout: f.base_layout, scope: f.scope });
      notify.success('フォーマット名を変更しました');
      cancelEdit();
    } catch (e: any) {
      notify.error(e?.message || '変更に失敗しました');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (f: TemplateFormat) => {
    const ok = await confirm({
      title: 'フォーマットの削除',
      message: `「${f.name}」を削除しますか？元に戻せません。`,
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove(f.id);
      notify.success('フォーマットを削除しました');
      if (editingId === f.id) cancelEdit();
    } catch (e: any) {
      notify.error(e?.message || '削除に失敗しました');
    }
  };

  const handlePreview = async (f: TemplateFormat) => {
    setPreviewingId(f.id);
    try {
      const result = await preview({ id: f.id, base_layout: f.base_layout });
      if (result) {
        // Drive に保存せず、その場でモーダル表示する
        if (result.blobUrl.startsWith('blob:')) {
          setPreviewState({ url: result.blobUrl, name: f.name });
        } else {
          // 後方互換: 旧仕様の Drive リンクは新しいタブで開く
          window.open(result.blobUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        notify.error('プレビューを取得できませんでした');
      }
    } catch (e: any) {
      notify.error(e?.message || 'プレビュー生成に失敗しました');
    } finally {
      setPreviewingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 1,
          px: 2,
          fontSize: '0.9rem',
          fontWeight: 600,
        }}
      >
        <Icons.PictureAsPdf sx={{ color: blueGrey[400], fontSize: 18 }} />
        帳票テンプレートフォーマット
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          新しいフォーマットの登録は、チャットの<strong>「レポート作成」モード</strong>で AI に依頼してください
          （例「この画像をフォーマットとして登録して」）。AI がレイアウトを設計し、名前を付けて登録します。
          この画面では、登録済みフォーマットの<strong>名前変更・削除・プレビュー</strong>ができます。
        </Alert>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: blueGrey[600], mb: 1 }}>
          登録済みフォーマット（{formats.length}）
        </Typography>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : formats.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            まだフォーマットがありません。チャットで AI に「フォーマットとして登録して」と依頼してください。
          </Typography>
        ) : (
          <List dense disablePadding>
            {formats.map((f) => {
              const isEditing = editingId === f.id;
              return (
                <ListItem
                  key={f.id}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, bgcolor: '#fff' }}
                  secondaryAction={
                    isEditing ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="保存">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRename(f)}
                              disabled={savingId === f.id}
                            >
                              {savingId === f.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Icons.Check sx={{ fontSize: 18, color: 'success.main' }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="キャンセル">
                          <IconButton size="small" onClick={cancelEdit}>
                            <Icons.Close sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="プレビュー">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handlePreview(f)}
                              disabled={previewingId === f.id}
                            >
                              {previewingId === f.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Icons.PictureAsPdf sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="名前を変更">
                          <IconButton size="small" onClick={() => startEdit(f)}>
                            <Icons.Edit sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton size="small" onClick={() => handleDelete(f)}>
                            <Icons.Delete sx={{ fontSize: 18, color: 'error.main' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  }
                >
                  <Tooltip
                    title={
                      f.scope === 'shared' && (f.shared_with?.length ?? 0) > 0
                        ? `共有メンバー: ${formatMembers(f.shared_with)}`
                        : ''
                    }
                  >
                    <Chip
                      size="small"
                      icon={f.scope === 'shared' ? <Icons.Groups /> : <Icons.Person />}
                      label={
                        f.scope === 'shared' ? `共有 ${f.shared_with?.length ?? 0}人` : '個人'
                      }
                      sx={{ fontSize: '0.65rem', height: 20, mr: 1 }}
                    />
                  </Tooltip>
                  {isEditing ? (
                    <TextField
                      size="small"
                      autoFocus
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleRename(f);
                        } else if (e.key === 'Escape') {
                          cancelEdit();
                        }
                      }}
                      inputProps={{ maxLength: 40 }}
                      sx={{ flex: 1, mr: 10, bgcolor: '#fff' }}
                    />
                  ) : (
                    <ListItemText
                      primary={f.name}
                      secondary={BASE_LAYOUT_LABELS[f.base_layout] ?? f.base_layout}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: '0.72rem' }}
                      sx={{ mr: 12 }}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 0.75 }}>
        <Button size="small" onClick={onClose} sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.78rem', py: 0.25 }}>
          閉じる
        </Button>
      </DialogActions>

      {/* プレビュー表示モーダル (Drive 非保存。PDF をその場で表示する) */}
      <Dialog open={!!previewState} onClose={closePreview} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            py: 1,
            px: 2,
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          <Icons.PictureAsPdf sx={{ color: blueGrey[400], fontSize: 18 }} />
          プレビュー — {previewState?.name}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#525659', height: '80vh' }}>
          {previewState && (
            <Box
              component="iframe"
              src={previewState.url}
              title="PDF プレビュー"
              sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 0.75 }}>
          <Button
            size="small"
            onClick={() => previewState && window.open(previewState.url, '_blank', 'noopener,noreferrer')}
            startIcon={<Icons.OpenInNew sx={{ fontSize: 16 }} />}
            sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.78rem', py: 0.25 }}
          >
            別タブで開く
          </Button>
          <Button
            size="small"
            onClick={closePreview}
            sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.78rem', py: 0.25 }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
